import { ScheduleData } from "@/components/global/Chat/components/ScheduleModal/types";

/**
 * One saved report.
 *
 * `id` is the report, and is what rename, schedule, run and remove address. A
 * report is one row from the moment the user saves it, with or without a
 * cadence — saving and scheduling are the same row, and `isScheduled` is the
 * difference between them.
 *
 * `originMessageId` is the chart the user saved: the run path replays it and
 * the preview renders its attachment. Keying a row on it instead of on `id`
 * collides the moment two reports are saved from one chart.
 *
 * The two preview fields go null together once the origin's attachment is
 * deleted. The report is still saved, still named and still runnable; there is
 * simply nothing to show until its next delivery, which
 * `lastReportAttachmentId` then carries.
 */
export interface SavedReport {
  id: string;
  name: string;
  createdAt: string;
  originMessageId: string;
  originAttachmentId: string | null;
  displayType: string | null;
  isReplayable: boolean;
  lastReportMessageId: string | null;
  lastReportAttachmentId: string | null;
  nextRunAt: string | null;
  isScheduled: boolean;
  scheduleConfig: ScheduleData | null;
}

/**
 * The fields opening a report in a preview addresses, which is fewer than a
 * whole row: a `SavedReport` satisfies this, and so does a schedule's last
 * delivery, which has no saved-report row of its own to pass.
 */
export type ReportPreviewTarget = Pick<
  SavedReport,
  "id" | "originMessageId" | "originAttachmentId" | "isScheduled"
> &
  Partial<Pick<SavedReport, "isReplayable" | "name">>;

/**
 * A report to preview when the chart it was saved from is not at hand. The
 * preview then shows the run's own result.
 */
export const reportTargetWithoutOrigin = (
  report: Pick<ReportPreviewTarget, "id" | "name"> &
    Partial<Pick<ReportPreviewTarget, "originMessageId" | "isReplayable">>
): ReportPreviewTarget => ({
  originMessageId: "",
  isReplayable: true,
  ...report,
  originAttachmentId: null,
  isScheduled: false
});

export interface SavedReportsPage {
  reports: SavedReport[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

/**
 * 202 body of the run-now endpoint. Both ids are always present: every saved
 * report has a row, so a run always has a report to belong to and a run row to
 * read back — including an ad-hoc run of a report that carries no cadence. The
 * result arrives as a `reportRun` user event; the run row is only read when
 * that event may have been missed.
 */
export interface RunNowAccepted {
  reportId: string;
  runId: string;
}

export type ReportRunTrigger = "manual" | "scheduled";

/** Where a run stands on the server. Only the last two are final. */
export type ReportRunServerStatus =
  | "pending"
  | "running"
  | "success"
  | "failed";

/** One run, as `GET /reports/saved/{reportId}/runs/{runId}` returns it. */
export interface ReportRunDetail {
  runId: string;
  reportId: string;
  status: ReportRunServerStatus;
  attachmentId: string | null;
  error: string | null;
  firedAt: string | null;
  completedAt: string | null;
  trigger: ReportRunTrigger;
}

/** The payload of a `reportRun` user event: a run that has finished. */
export interface ReportRunEventPayload {
  reportId: string;
  runId: string;
  trigger: ReportRunTrigger;
  status: "success" | "failed";
  attachmentId: string | null;
  reportName: string;
  completedAt: string | null;
}

export interface ReportsUnseenCount {
  count: number;
}
